import PropTypes from "prop-types";
// @mui
import { socket } from "src/App";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { shouldSkipLocalKitchenSlip } from "src/utils/posLayout/kitchenReceiptMasterAccountUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack, AppBar, Button, Toolbar, IconButton, FormControl, Select, MenuItem, Typography } from "@mui/material";
// utils
import { bgBlur } from "../../../utils/cssStyles";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// hooks
import useOffSetTop from "../../../hooks/useOffSetTop";
import useResponsive from "../../../hooks/useResponsive";
// config
import { HEADER, NAV } from "../../../config-global";
// components
import Logo from "../../../components/logo";
import Iconify from "../../../components/iconify";
import { useSettingsContext } from "../../../components/settings";
//
import AccountPopover from "./AccountPopover";
import LevelPopover from "./LevelPopover";
import UnsyncedTaxInvoicesButton from "./UnsyncedTaxInvoicesButton";
import { useAuthContext } from "src/auth/useAuthContext";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "@mui/system";
import axios from "axios";
import { getRealTimeOrdersCount } from "src/redux/slices/ordersSlice";
import { playNotificationSound } from "src/utils/notificationSound";
import { updateOrdersCountInSelectedKDScreens } from "src/redux/slices/kitchenSlice";
import { getNotificationSounds } from "src/utils/notificationSounds";
import Label from "src/components/label";
import { filterOutVoidItems } from "src/utils/filterVoidItems";
import { setLastAlertTime } from "src/redux/slices/soundAlertSlice";
import { startRepeatingAlert, setupAlertTimerOnRefresh } from "src/utils/startRepeatingAlert";
import ConfirmDialog from "src/components/confirm-dialog";
import { detectBrowser, getAudioInstructions } from "src/utils/browserAudioHelper";
import { flatMap } from "lodash";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

Header.propTypes = {
  onOpenNav: PropTypes.func,
};

export default function Header({ onOpenNav }) {

  const navigate = useNavigate();
  const { user, updateUser } = useAuthContext();
  const { venue, permissions, incomingOrdersAlertTime } = user ?? {}
  const { enableAlertSound, enableAlertSoundForIncomingOrders, canSeeKitchenScreen, canSeeOrderProcessingScreen } = permissions ?? {}
  const { configurations } = venue ?? {}
  const { isOrderingFeatureAvailed, isKitchenFeatureAvailed, isKitchenReceiptPrinterIPConfigEnabled, isBillAndRiderReceiptPrinterIPConfigEnabled, isThermalReportPrinterIPConfigEnabled } = configurations || {};
  const theme = useTheme();
  const dispatch = useDispatch();
  const { themeLayout } = useSettingsContext();
  const { pathname, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const tableId = queryParams.get("table"); // Get the "table" parameter

  const isNavHorizontal = themeLayout === "horizontal";
  const isNavMini = themeLayout === "mini";
  const isDesktop = useResponsive("up", "lg");
  const isMobile = useResponsive("up", "md");
  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;
  const isCalendar = () => pathname === "/dashboard/reservation/calendar" ? true : false;
  const isPOS = () => (pathname === "/dashboard/pos" ? true : false);
  const isTables = () => (pathname === "/dashboard/tables" ? true : false);
  const isOrders = () => (pathname === "/dashboard/orders" ? true : false);
  const isKitchenScreen = () => pathname === "/dashboard/kitchen" ? true : false;
  const [isMuted, setIsMuted] = useState(enableAlertSound)
  const [isServiceUp, setIsServiceUp] = useState(false);
  const handleLevelChange = () => { };
  const { orderCount } = useSelector((state) => state.ordersSlice);
  const { pendingOrders } = orderCount ?? {}
  const shiftStartTime = useSelector(
      (state) => state.shift.shiftStartTime
    );
  const isShiftStarted = Boolean(shiftStartTime);
  
  useEffect(() => {
    if (pendingOrders === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      dispatch(setLastAlertTime(null));
    }
  }, [pendingOrders, dispatch]);

  const { ordersCountInSlecetedKds, selectedKds, kitchenDisplayScreens } = useSelector((state) => state.kitchenSlice);

  useEffect(() => {
    const fetchOrderCounts = async () => {
      try {
        const getToken = sessionStorage.getItem("accessToken");
        await dispatch(getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken))
      } catch (error) {
        console.error("Failed to fetch order counts:", error);
      }
    };
    fetchOrderCounts();
    if (user.venue.configurations.isKitchenFeatureAvailed) {
      const getToken = sessionStorage.getItem("accessToken");
      dispatch(updateOrdersCountInSelectedKDScreens(user?.venueId, user?.levelId, getToken, user))
    }
  }, []);


  useEffect(() => {
    if (
      isKitchenReceiptPrinterIPConfigEnabled ||
      isBillAndRiderReceiptPrinterIPConfigEnabled ||
      isThermalReportPrinterIPConfigEnabled
    ) {
      const checkService = async () => {
        try {
          const url = process.env.REACT_APP_PRINT_SERVICE_URL;
          const response = await fetch(`${url}/status`, { method: "HEAD" });
          setIsServiceUp(response.ok);
        } catch (err) {
          setIsServiceUp(false);
        }
      };
      checkService();
      const interval = setInterval(checkService, 30000);
      return () => clearInterval(interval);
    }
  }, [
    isKitchenReceiptPrinterIPConfigEnabled,
    isBillAndRiderReceiptPrinterIPConfigEnabled,
    isThermalReportPrinterIPConfigEnabled
  ]);

  // Kitchen receipt master account (venue setting): child users' slips print on the master account's device,
  // so their icon is green without a local print service; the master account itself uses its own /status check.
  const isPrinterIconGreen = shouldSkipLocalKitchenSlip(user) || isServiceUp;

  const notificationSounds = getNotificationSounds()
  const selectedSound = user?.notificationSound || "egoraSound1";

  const { lastAlertTime } = useSelector(state => state.soundAlert);
  const timeoutRef = useRef(null);
  const hasMountedRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!user?.permissions?.enableAlertSound) return;
    setupAlertTimerOnRefresh({ lastAlertTime, user, selectedSound, notificationSounds, dispatch, setLastAlertTime, timeoutRef });
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) };
  }, [user, isMuted])


  useEffect(() => {

    socket.on("processingOrdersForPOS", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          ((user.venue?.configurations?.isOrderingFeatureAvailed || user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen || user.permissions?.canSeeKitchenScreen)) || user?.permissions?.canTakePaymentOnPOS
        ) {
          let playSound = false
          playSound = playNotificationSound(data, selectedKds, kitchenDisplayScreens)
          if(user.permissions?.canSeeOrderProcessingScreen || user?.permissions?.canTakePaymentOnPOS){
            playSound = true
          }
          if (user?.permissions?.enableAlertSound && playSound) {
            const audio = new Audio(notificationSounds[selectedSound]);
            audio.play().catch((e) => console.error("Audio play failed:", e));
          }
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

    socket.on("processingOrderClear", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isPOSAvailable ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

    socket.on("OrderDeleted", (orderData) => {
      if (
        (user.venueId === orderData.venueId.id ||
          user.venueId === orderData.venueId) &&
        (user.levelId === orderData.levelId || user?.levelId === orderData.levelId?.id ||
          user.role == "admin" ||
          orderData.isSuperAdmin)
      ) {
        const getToken = sessionStorage.getItem("accessToken");
        dispatch(
          getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
        );
      }
    });

    socket.on("updateOrder", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isOrderingFeatureAvailed ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          let nonVoidedItems = filterOutVoidItems(data.items)
          const hasUnpreparedItems = nonVoidedItems.some(
            (item) => !item.isPrepared
          );
          if (data.isPaid == false || hasUnpreparedItems) {
            let playSound = playNotificationSound(data, selectedKds, kitchenDisplayScreens)
            if (user?.permissions?.enableAlertSound && playSound) {
              const audio = new Audio(notificationSounds[selectedSound]);
              audio.play().catch((e) => console.error("Audio play failed:", e));
            }
            const getToken = sessionStorage.getItem("accessToken");
            dispatch(
              getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
            );
          }
        }
      }
    });

    socket.on("processingOrderClear", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isOrderingFeatureAvailed ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          let playSound = playNotificationSound(data, selectedKds, kitchenDisplayScreens)
          if (user?.permissions?.enableAlertSound && playSound) {
            const audio = new Audio(notificationSounds[selectedSound]);
            audio.play().catch((e) => console.error("Audio play failed:", e));
          }
        }
      }
    });

    socket.on("cancelOrder", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isPOSAvailable ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

    socket.on("completedOrderRemove", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isPOSAvailable ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

    socket.on("IncomingOrdersForPos", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isOrderingFeatureAvailed ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          if (user?.permissions?.enableAlertSound) {
            const audio = new Audio(notificationSounds[selectedSound]);
            audio.play().catch(e => console.error("Audio play failed:", e));
            if (user?.permissions?.enableAlertSoundForIncomingOrders) {
              const seconds = user?.incomingOrdersAlertTime || 30;
              dispatch(setLastAlertTime(new Date().toISOString()));
              startRepeatingAlert(timeoutRef, seconds, selectedSound, notificationSounds, dispatch, setLastAlertTime);
            }
          }
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

    socket.on("IncomingOrderCancel", (data) => {
      if (
        (user?.venueId === data.venueId.id || user?.venueId === data.venueId) &&
        (user?.levelId === data.levelId || user?.levelId === data.levelId?.id ||
          user?.role == "admin" ||
          data.isSuperAdmin)
      ) {
        if (
          (user.venue?.configurations?.isOrderingFeatureAvailed ||
            user.venue?.configurations?.isKitchenFeatureAvailed) &&
          (user.permissions?.canSeeOrderProcessingScreen ||
            user.permissions?.canSeeKitchenScreen)
        ) {
          if (user?.permissions?.enableAlertSound) {
            const audio = new Audio(notificationSounds[selectedSound]);
            audio.play().catch((e) => console.error("Audio play failed:", e));
          }
          const getToken = sessionStorage.getItem("accessToken");
          dispatch(
            getRealTimeOrdersCount(user?.venueId, user?.levelId, getToken)
          );
        }
      }
    });

  }, [socket]);

  const handleGoToPOS = () => {
    if (!tableId) { navigate(PATH_DASHBOARD.pos.list, { replace: true }) }
  };

  const handleGoToReservations = () => {
    navigate(PATH_DASHBOARD.reservation.calendar, { replace: true });
  };

  const handleGoToTablesScreen = () => {
    navigate(PATH_DASHBOARD.tables.list, { replace: true });
  };

  const handleGoToKitchenScreen = () => {
    navigate(PATH_DASHBOARD.kitchen.list, { replace: true });
  };

  const handleGoToOrders = () => {
    navigate(PATH_DASHBOARD.orders.list, { replace: true });
  };

  const tabMappings = {
    pos: {
      condition:
        user?.venue?.configurations?.isPOSAvailable &&
        user.permissions?.canViewPos,
      path: "/dashboard/pos",
      action: handleGoToPOS,
    },
    reservation: {
      condition:
        user?.venue?.configurations?.isReservation &&
        user.permissions?.canSeeReservationCalendar,
      path: "/dashboard/reservation/calendar",
      action: handleGoToReservations,
    },
    tables: {
      condition:
        user?.venue?.configurations?.isTableFeatureAvailed &&
        user?.permissions?.canViewTable,
      path: "/dashboard/tables",
      action: handleGoToTablesScreen,
    },
    orders: {
      condition:
        user?.venue?.configurations?.isOrderingFeatureAvailed &&
        user?.permissions?.canSeeOrderProcessingScreen,
      path: "/dashboard/orders",
      action: handleGoToOrders,
    },
    kitchen: {
      condition:
        user?.venue?.configurations?.isKitchenFeatureAvailed &&
        user?.permissions?.canSeeKitchenScreen,
      path: "/dashboard/kitchen",
      action: handleGoToKitchenScreen,
    },
  };

  useEffect(() => {
    if (
      [
        "/dashboard/pos",
        "/dashboard/reservation/calendar",
        "/dashboard/tables",
        "/dashboard/orders",
        "/dashboard/kitchen",
      ].includes(pathname)
    ) {

      const availableTabs = Object.entries(tabMappings)
        .filter(([_, { condition, path }]) => condition && pathname === path)
        .map(([key]) => key);

      if (!availableTabs.length) {
        availableTabs.push(
          ...Object.keys(tabMappings).filter((key) => tabMappings[key].condition)
        );
      }
      if (availableTabs.length > 0) {
        tabMappings[availableTabs[0]].action();
      }
    }
  }, [pathname, user]);

  const tabs = [
    {
      label: "POS",
      permission: "canViewPos",
      configuration: "isPOSAvailable",
      onClick: handleGoToPOS,
    },
    {
      label: "Reservation",
      permission: "canSeeReservationCalendar",
      configuration: "isReservation",
      onClick: handleGoToReservations,
    },
    {
      label: "Tables",
      permission: "canViewTable",
      configuration: "isTableFeatureAvailed",
      disabled: !isShiftStarted && user?.venue?.configurations?.isShiftAvailable , 
      onClick: handleGoToTablesScreen,
    },
    {
      label: "Orders",
      permission: "canSeeOrderProcessingScreen",
      configuration: "isOrderingFeatureAvailed",
      onClick: handleGoToOrders,
      orderCount: (
        <span>
          {user?.venue?.configurations?.isPickUp || user?.venue?.configurations?.isDineIn || user?.venue?.configurations?.isAvailableOnStore ? (
            <span>
              {orderCount.pendingOrders} / {orderCount.processingOrders} /{" "}
              {orderCount.finishedOrders}
            </span>
          ) : (
            <span>
              {orderCount.processingOrders} / {orderCount.finishedOrders}
            </span>
          )}
        </span>
      ),
    },
    {
      label: "Kitchen",
      permission: "canSeeKitchenScreen",
      configuration: "isKitchenFeatureAvailed",
      onClick: handleGoToKitchenScreen,
      orderCount: (
        <span>
          {" "}
          {ordersCountInSlecetedKds}{" "}
        </span>
      ),
    },
  ];

  const isActiveTab = (tab) => {
    switch (tab.label) {
      case "POS":
        return isPOS();
      case "Reservation":
        return isCalendar();
      case "Tables":
        return isTables();
      case "Orders":
        return isOrders();
      case "Kitchen":
        return isKitchenScreen();
      default:
        return false;
    }
  };

  const calculateRightProperty = (totalTabsCount) => {
    switch (totalTabsCount) {
      case 5:
        return 40;
      case 4:
        return 30;
      case 3:
        return 20;
      case 2:
        return 10;
      case 1:
        return 0;
      default:
        return 0;
    }
  };

  const handleBellClick = async () => {
    const token = sessionStorage.getItem("accessToken");

    try {
      const response = await axios.patch(
        `${backendUrl}/venueUsers/${user?.id}/updateEnableAlertSound`,
        {
          enableAlertSound: !isMuted,
          enableAlertSoundForIncomingOrders: enableAlertSoundForIncomingOrders,
          incomingOrdersAlertTime: Number(incomingOrdersAlertTime),
          notificationSound: selectedSound,
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      const updatedUser = response?.data;
      updateUser(updatedUser);
      setIsMuted(!isMuted);
      if (!isMuted) {
        setupAlertTimerOnRefresh({ lastAlertTime, user: { ...user, permissions: { ...user.permissions, enableAlertSound: true } }, selectedSound, notificationSounds, dispatch, setLastAlertTime, timeoutRef });
      } else {
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null }
      }

  } catch (error) {
    console.error(error);
  }
};

  const [browser, setBrowser] = useState("Chrome");
  const [instructions, setInstructions] = useState(getAudioInstructions("Chrome"));
  const [openBrowserModal, setOpenBrowserModal] = useState(false);
  const onClose = () => setOpenBrowserModal(false)

  useEffect(() => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();    
    if ((audioContext?.state === "suspended") && enableAlertSound && (isKitchenFeatureAvailed || isOrderingFeatureAvailed)) {
      setOpenBrowserModal(true)
      const detectedBrowser = detectBrowser();
      setBrowser(detectedBrowser);
      setInstructions(getAudioInstructions(detectedBrowser));
    }
  }, []);

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: "text.primary" }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {(isPOS() || isCalendar() || isTables() || isOrders() || isKitchenScreen()) && (
        <Stack
          flexGrow={1}
          zIndex={100}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={{ xs: 0, sm: 0 }}
          marginRight={2}
        >
          {!isMobile ? (
            // Mobile Dropdown Version
            <FormControl sx={{ width: "100%" }}>
              <Select
                value={tabs.find(tab => isActiveTab(tab))?.label || ''}
                onChange={(e) => {
                  const selectedTab = tabs.find(tab => tab.label === e.target.value);
                  if (selectedTab) selectedTab.onClick();
                }}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                sx={{
                  fontWeight: "bold",
                  borderRadius: "20px 20px 0px 0px",
                  backgroundColor: '#fda92d',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    py: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      marginTop: '4px',
                      '& .MuiMenuItem-root': {
                        fontWeight: 'bold',
                        '&.Mui-selected': {
                          backgroundColor: '#fda92d',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#e69527',
                        },
                      },
                    },
                  },
                }}
              >
                {tabs
                  .filter((tab) => {
                    const condition = user?.venue?.configurations[tab.configuration];
                    const permission = user.permissions?.[tab.permission];
                    
                    return condition && permission;
                  })
                  .map((tab, index) => (
                    <MenuItem
                      key={index}
                      value={tab.label}
                       disabled={tab.disabled}
                      
                      sx={{
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        '&.Mui-selected': {
                          backgroundColor: '#fda92d',
                          '& .MuiLabel-root': {
                            backgroundColor: '#ffffff33',
                          },
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center">
                        {tab.label}
                        {/* {tab.label === 'Reservation' && (
                          <Label
                            sx={{
                              // ml: 1,
                              textTransform: 'lowercase',
                              position: 'relative',
                              left: '10px',
                              backgroundColor: '#f8ecd6',
                              color: '#b76e00'
                            }}
                          >
                            beta
                          </Label>
                        )} */}
                        {tab.orderCount && (
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: "bold" }}>
                            ({tab.orderCount})
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : (
            // Desktop Buttons Version
            <>
              {tabs
                .filter((tab) => {
                  const condition = user?.venue?.configurations[tab.configuration];
                  const permission = user.permissions?.[tab.permission];
                  return condition && permission;
                })
                .map((tab, index) => (
                  <Button
                    key={index}
                    size="medium"
                    type="submit"
                    variant={isActiveTab(tab) ? "contained" : "outlined"}
                    onClick={tab.onClick}
                    disabled={tab.disabled}
                    sx={{
                      width: "100%",
                      borderRadius: "20px 20px 0px 0px",
                      bgcolor: !isActiveTab(tab) ? "white" : "",
                      zIndex: isActiveTab(tab)
                        ? tabs.length
                        : tabs.length - index - 1,
                      right:
                        calculateRightProperty(tabs.length) -
                        (tabs.length - index - 1) * 10,
                      '@media (min-width: 768px) and (max-width: 1024px)': {
                        width: tab.label == 'Reservation' ? '160%' : '100%', // Color between 768px and 1024px
                      },
                      
                    }}
                  >
                    {tab.label}
                    {/* {
                      tab.label == 'Reservation' ?
                        <Label
                          style={{
                            textTransform: 'lowercase',
                            position: 'relative',
                            left: '10px',
                            backgroundColor: '#f8ecd6',
                            color: '#b76e00'
                          }}>beta</Label>
                        : null
                    } */}
                    {tab.orderCount && (
                      <span
                        style={{
                          marginLeft: "2px",
                        }}
                      >
                        ({tab?.orderCount})
                      </span>
                    )}
                  </Button>
                ))}
            </>
          )}
        </Stack>
      )}

      <Stack
        flexGrow={
          isPOS() ||
            isTables() ||
            isCalendar() ||
            isOrders() ||
            isKitchenScreen()
            ? ""
            : 1
        }
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        <LevelPopover onLevelChange={handleLevelChange} />
        {(isKitchenReceiptPrinterIPConfigEnabled || isBillAndRiderReceiptPrinterIPConfigEnabled || isThermalReportPrinterIPConfigEnabled) && (
            <Iconify
              sx={{
                width: 25, height: 25,
                color: isPrinterIconGreen ? "green" : "red"
              }}
              icon={"mdi:printer-outline"}
            />
        )}
        {((isOrderingFeatureAvailed && canSeeOrderProcessingScreen) || (isKitchenFeatureAvailed && canSeeKitchenScreen)) && (
          <IconButton onClick={handleBellClick}>
            <Iconify
              sx={{ width: 25, height: 25 }}
              icon={isMuted ? "mdi:bell-ring" : "mdi:bell-off"}
            />
          </IconButton>
        )}
          <UnsyncedTaxInvoicesButton/>
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <>
      <AppBar
        sx={{
          boxShadow: "none",
          height: HEADER.H_MOBILE,
          zIndex: theme.zIndex.appBar + 1,
          ...bgBlur({
            color: theme.palette.background.default,
          }),
          transition: theme.transitions.create(["height"], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(isDesktop && {
            width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
            height: HEADER.H_DASHBOARD_DESKTOP,
            ...(isOffset && {
              height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            }),
            ...(isNavHorizontal && {
              width: 1,
              bgcolor: "background.default",
              height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
              borderBottom: `dashed 1px ${theme.palette.divider}`,
            }),
            ...(isNavMini && {
              width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
            }),
          }),
        }}
      >
        <Toolbar
          sx={{
            height: 1,
            px: { lg: 5 },
          }}
        >
          {renderContent}
        </Toolbar>
      </AppBar>
      {browser === "Chrome" && (
        <ConfirmDialog
          open={openBrowserModal}
          onClose={onClose}
          title={`Enable Audio in ${browser}`}
          content={
            <ul style={{ paddingLeft: 20 }}>
              {instructions.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          }
          action={
            <Button variant="contained" color="primary" onClick={onClose}>
              Ok
            </Button>
          }
        />
      )}
    </>
  );
}
